import React, { useState } from 'react';
import './QuickAccess.css';

function QuickAccess({ onClose }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (!username.trim() || !password.trim()) {
        setError('Please enter both username and password');
        setIsLoading(false);
        return;
      }

      // In development, React dev server runs on different port, so use full URL
      // In production, both are on same server, so use relative URL
      const isDevelopment = window.location.hostname === 'localhost' && window.location.port !== '3000';
      const apiBaseUrl = isDevelopment ? 'http://localhost:3000' : '';
      
      const response = await fetch(`${apiBaseUrl}/api/users/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username.trim(),
          password: password
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Login successful
        const authToken = `${username.trim()}-${Date.now()}`;
        localStorage.setItem('chat_auth_token', authToken);
        localStorage.setItem('chat_username', username.trim());
        sessionStorage.setItem('guest_access', 'true');
        
        // Redirect to dashboard
        window.location.href = '/dashboard';
      } else {
        setError(data.error || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      setError('Unable to connect to server. Please try again later.');
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccessCode = async (code) => {
    setError(null);
    setIsLoading(true);

    try {
      if (!code.trim()) {
        setError('Please enter an access code');
        setIsLoading(false);
        return;
      }

      // Check if it's an admin code
      if (code.includes('admin-') || code.includes('_')) {
        // Redirect to admin page
        window.location.href = `/admin-${code}`;
        return;
      }

      // Try to validate as invite code
      // In development, React dev server runs on different port, so use full URL
      // In production, both are on same server, so use relative URL
      const isDevelopment = window.location.hostname === 'localhost' && window.location.port !== '3000';
      const apiBaseUrl = isDevelopment ? 'http://localhost:3000' : '';
      
      const codeToValidate = encodeURIComponent(code.trim().toUpperCase());
      const response = await fetch(`${apiBaseUrl}/api/invite-codes/validate/${codeToValidate}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Check if response is ok
      if (!response.ok && response.status !== 400 && response.status !== 503) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();
      
      console.log('Access code validation response:', data);
      console.log('allowWithoutRegistration value:', data.allowWithoutRegistration, 'type:', typeof data.allowWithoutRegistration);

      if (response.ok && data.valid) {
        // Check if code is active (new requirement: codes start inactive, become active after registration)
        if (data.active === false) {
          // Code is inactive - requires registration first
          setError('This access code requires registration. Please register first.');
          setTimeout(() => {
            window.location.href = `/?register=true&code=${encodeURIComponent(code.trim().toUpperCase())}`;
          }, 2000);
          setIsLoading(false);
          return;
        }

        // Code is active - proceed with normal flow
        // First, check if a user already exists with this access code
        try {
          const usersResponse = await fetch(`${apiBaseUrl}/api/users`);
          if (usersResponse.ok) {
            const usersData = await usersResponse.json();
            const user = usersData.users?.find(u => u.inviteCode && u.inviteCode.toUpperCase() === code.trim().toUpperCase());
            
            if (user) {
              // User found with this access code - log them in directly
              localStorage.setItem('chat_username', user.username);
              localStorage.setItem('chat_auth_token', `${user.username}-${Date.now()}`);
              sessionStorage.setItem('guest_access', 'true');
              sessionStorage.setItem('access_code', code.trim().toUpperCase());
              window.location.href = '/dashboard';
              setIsLoading(false);
              return;
            }
          }
        } catch (userError) {
          console.error('Error finding user by access code:', userError);
          // Continue to check allowWithoutRegistration
        }
        
        // No user found - check if code allows access without registration
        if (data.allowWithoutRegistration === true) {
          // Code allows quick access without registration - store access and redirect to dashboard
          sessionStorage.setItem('guest_access', 'true');
          sessionStorage.setItem('access_code', code.trim().toUpperCase());
          window.location.href = '/dashboard';
        } else {
          // Code requires registration - redirect to registration page with code
          setError('This access code requires registration. Please register first.');
          setTimeout(() => {
            window.location.href = `/?register=true&code=${encodeURIComponent(code.trim().toUpperCase())}`;
          }, 2000);
        }
      } else if (response.status === 503) {
        // Database not configured
        setError('Database not configured. Please contact administrator.');
      } else {
        setError(data.error || 'Invalid access code. Please check and try again.');
      }
    } catch (err) {
      console.error('Access code error:', err);
      if (err.message && err.message.includes('Failed to fetch')) {
        setError('Unable to connect to server. Please ensure the server is running on port 3000.');
      } else {
        setError(err.message || 'Unable to connect to server. Please try again later.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Determine which method to use based on filled fields
    const hasLogin = username.trim() && password.trim();
    const hasAccessCode = accessCode.trim();

    if (!hasLogin && !hasAccessCode) {
      setError('Please enter either username/password or an access code');
      return;
    }

    if (hasLogin && hasAccessCode) {
      setError('Please enter either username/password OR access code, not both');
      return;
    }

    if (hasLogin) {
      await handleLogin(e);
    } else if (hasAccessCode) {
      await handleAccessCode(accessCode);
    }
  };

  return (
    <div className="quick-access-overlay" onClick={onClose}>
      <div className="quick-access-modal" onClick={(e) => e.stopPropagation()}>
        <div className="quick-access-header">
          <h2>Quick Access</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="quick-access-content">
          <form onSubmit={handleSubmit} className="quick-access-form">
            <div className="form-divider">
              <span>Login with Username & Password</span>
            </div>

            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                disabled={isLoading}
                autoFocus
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                disabled={isLoading}
              />
            </div>

            <div className="form-divider">
              <span>OR</span>
            </div>

            <div className="form-group">
              <label htmlFor="accessCode">Access Code</label>
              <input
                type="text"
                id="accessCode"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                placeholder="Enter your access code"
                disabled={isLoading}
              />
              <p className="form-hint">
                Enter an access code to access the dashboard, or an admin code for admin access
              </p>
            </div>

            {error && (
              <div className="error-message">{error}</div>
            )}

            <button
              type="submit"
              className="submit-button"
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : 'Access'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default QuickAccess;
